import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Globe2, Menu, X } from "lucide-react";
import { LANGS, useLang } from "@/i18n/LanguageContext";
import { scrollToId } from "@/App";

const SECTIONS = ["destinations", "services", "alpha-guide", "app", "trust"];

export default function Navbar() {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSection = (id) => {
    setMenuOpen(false);
    if (location.pathname !== "/") navigate(`/#${id}`);
    else scrollToId(id);
  };

  const labelFor = (id) =>
    id === "destinations" ? t("nav.destinations")
    : id === "services" ? t("nav.services")
    : id === "alpha-guide" ? t("nav.guide")
    : id === "app" ? t("nav.app") : t("nav.trust");

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-[80] transition-all duration-500 ${scrolled ? "border-b border-white/10 bg-[#050814]/80 backdrop-blur-xl" : "bg-transparent"}`}
    >
      <div className="mx-auto flex h-[70px] max-w-[1500px] items-center justify-between px-6 lg:px-12">
        <Link to="/" data-testid="nav-logo" className="flex items-baseline gap-2">
          <span className="font-serif text-2xl font-medium tracking-tight text-white">Aurevia</span>
          <span className="font-mono text-[10px] tracking-[0.4em] text-[#d4af37]">TRAVELLA</span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex">
          {SECTIONS.map((id) => (
            <button
              key={id}
              data-testid={`nav-link-${id}`}
              onClick={() => goSection(id)}
              className="gold-underline text-[13px] font-medium tracking-wide text-white/75 hover:text-white"
            >
              {labelFor(id)}
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <div className="relative">
            <button
              data-testid="lang-switcher"
              onClick={() => setLangOpen((v) => !v)}
              className="flex items-center gap-2 border border-white/15 px-3 py-1.5 font-mono text-[11px] tracking-[0.2em] text-white/80 transition-colors hover:border-[#d4af37]/60 hover:text-[#e8cf7a]"
            >
              <Globe2 size={14} />
              {lang.toUpperCase()}
            </button>
            <AnimatePresence>
              {langOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.2 }}
                  className="absolute right-0 top-full mt-2 w-40 border border-white/10 bg-[#0a1128]/95 backdrop-blur-xl"
                  data-testid="lang-menu"
                >
                  {LANGS.map((l) => (
                    <button
                      key={l.code}
                      data-testid={`lang-option-${l.code}`}
                      onClick={() => { setLang(l.code); setLangOpen(false); }}
                      className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-sm transition-colors hover:bg-white/5 ${lang === l.code ? "text-[#d4af37]" : "text-white/70"}`}
                    >
                      {l.name}
                      <span className="font-mono text-[10px] tracking-widest opacity-60">{l.label}</span>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <button
            data-testid="nav-cta"
            onClick={() => goSection("concierge")}
            className="btn-gold px-5 py-2 text-[13px] font-semibold tracking-wide"
          >
            {t("nav.cta")}
          </button>
        </div>

        <button
          data-testid="mobile-menu-button"
          className="text-white lg:hidden"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Menu"
        >
          {menuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden border-b border-white/10 bg-[#050814]/95 backdrop-blur-xl lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {SECTIONS.map((id) => (
                <button
                  key={id}
                  data-testid={`mobile-nav-link-${id}`}
                  onClick={() => goSection(id)}
                  className="py-3 text-left font-serif text-2xl text-white/85"
                >
                  {labelFor(id)}
                </button>
              ))}
              <div className="mt-4 flex flex-wrap gap-2">
                {LANGS.map((l) => (
                  <button
                    key={l.code}
                    data-testid={`mobile-lang-option-${l.code}`}
                    onClick={() => setLang(l.code)}
                    className={`border px-3 py-1.5 font-mono text-[11px] tracking-widest ${lang === l.code ? "border-[#d4af37] text-[#d4af37]" : "border-white/15 text-white/60"}`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
              <button
                data-testid="mobile-nav-cta"
                onClick={() => goSection("concierge")}
                className="btn-gold mt-6 px-5 py-3 text-sm font-semibold"
              >
                {t("nav.cta")}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
